import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDbState, UserBlobErrorBanner } from '../hooks/useUserBlob';

// Weekly Time Audit & Capacity Review.
//
// Lets each commercial team member log how their week is spent at a task
// granularity: duration × frequency, strategic vs reactive split, value
// rating, role-fit, suggested action. Rows can be reordered via drag-and-
// drop and grouped under section headers. Persisted per-user in
// localStorage so it survives reloads. Exports the active person's tasks
// (with their group label) to a CSV that Excel opens cleanly.

// API blob keys MUST avoid `/` because the Azure Functions route is
// `user-blob/{key}` — a single path segment. Use `:` instead (the API
// regex `[a-z0-9_:.-]` already accepts it). Legacy localStorage keys
// kept the `/` form; we read from both during migration.
const STORAGE_KEY = 'capacityReview:v1';
const NOTES_STORAGE_KEY = 'capacityReview:notes:v1';
const ROLES_STORAGE_KEY = 'capacityReview:roles:v1';
const LEGACY_LS_STORAGE_KEY = 'capacityReview/v1';
const LEGACY_LS_NOTES_KEY = 'capacityReview/notes/v1';
const LEGACY_LS_ROLES_KEY = 'capacityReview/roles/v1';

// Default seed used the first time the page loads (or if the stored roles
// list is wiped). The pill nav up top is keyed by role; each role then has
// an editable person name + standard contracted hours that drive the
// "available" KPI on the summary cards. Users can add or remove roles.
const DEFAULT_ROLES = [
  { id: 'matthew', name: 'Matthew Blake', role: 'National Sales Manager',     standardHours: 38 },
  { id: 'rhys',    name: 'Rhys Holbeck',  role: 'National Account Manager',   standardHours: 38 },
  { id: 'nina',    name: 'Nina Warwick',  role: 'Business Executive',         standardHours: 38 },
];

// Phase 9: data now lives in commercial.UserBlob (per-user, multi-key store).
// Legacy loaders below are used ONCE on first API hydration to migrate any
// existing localStorage data into the DB, then the localStorage key is removed.
function loadLegacyRoles() {
  try {
    // Try the legacy slash key first, then the new colon key (covers users
    // who upgraded between the slash-bug release and this fix).
    const raw = localStorage.getItem(LEGACY_LS_ROLES_KEY)
             || localStorage.getItem(ROLES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      try { localStorage.removeItem(LEGACY_LS_ROLES_KEY); } catch { /* ignore */ }
      try { localStorage.removeItem(ROLES_STORAGE_KEY); } catch { /* ignore */ }
      return parsed;
    }
    return null;
  } catch { return null; }
}

const CATEGORIES   = ['Strategic', 'Operational', 'Reactive', 'Admin', 'Meetings', 'Other'];
const VALUE_LEVELS = ['High', 'Medium', 'Low', 'Duplicated'];
const ROLE_FITS    = ['Aligned', 'Mismatch'];
const ACTIONS      = ['Keep', 'Delegate', 'Simplify', 'Eliminate', 'Clarify'];

// Frequency → weekly-equivalent multiplier, e.g. a 1hr fortnightly meeting
// rolls up to 0.5 hrs/week of capacity. "Ad hoc" doesn't have a fixed
// cadence — it counts the duration as-is (treat as a one-off this week).
const FREQUENCIES = [
  { id: 'weekly',      label: 'Weekly',      multiplier: 1 },
  { id: 'fortnightly', label: 'Fortnightly', multiplier: 1 / 2 },
  { id: 'monthly',     label: 'Monthly',     multiplier: 12 / 52 },
  { id: 'quarterly',   label: 'Quarterly',   multiplier: 4 / 52 },
  { id: 'halfyearly',  label: 'Half-yearly', multiplier: 2 / 52 },
  { id: 'annual',      label: 'Annual',      multiplier: 1 / 52 },
  { id: 'adhoc',       label: 'Ad hoc',      multiplier: 1 },
];
const FREQ_BY_ID = Object.fromEntries(FREQUENCIES.map(f => [f.id, f]));

function rid() { return Math.random().toString(36).slice(2, 9); }

// Convert a task row to its weekly-equivalent hours. Section rows return 0.
// Tolerates legacy rows that only had `hours` (pre-frequency schema).
function weeklyHours(t) {
  if (t.kind === 'section') return 0;
  if (t.duration != null) {
    const f = FREQ_BY_ID[t.frequency] || FREQ_BY_ID.weekly;
    return (Number(t.duration) || 0) * f.multiplier;
  }
  return Number(t.hours) || 0;
}

const isTask    = (r) => r && r.kind !== 'section';
const isSection = (r) => r && r.kind === 'section';

// Seed entries for first-time users — gives a feel for the structure
// without committing them to a particular workflow.
const SEED_TASKS = () => [
  { id: rid(), kind: 'section', label: 'Customer & Sales' },
  { id: rid(), task: 'Weekly retailer sales review', duration: 3, frequency: 'weekly',      category: 'Operational', value: 'High',   roleFit: 'Aligned',  action: 'Keep',     notes: '' },
  { id: rid(), task: 'Forecast meeting prep',        duration: 2, frequency: 'fortnightly', category: 'Strategic',   value: 'High',   roleFit: 'Aligned',  action: 'Keep',     notes: '' },
  { id: rid(), kind: 'section', label: 'Reactive / Admin' },
  { id: rid(), task: 'Inbox / email triage',         duration: 6, frequency: 'weekly',      category: 'Reactive',    value: 'Medium', roleFit: 'Mismatch', action: 'Simplify', notes: 'Could batch twice a day' },
];

function loadLegacyAll() {
  try {
    const raw = localStorage.getItem(LEGACY_LS_STORAGE_KEY)
             || localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      try { localStorage.removeItem(LEGACY_LS_STORAGE_KEY); } catch { /* ignore */ }
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      return parsed;
    }
    return null;
  } catch { return null; }
}

function loadLegacyNotes() {
  try {
    const raw = localStorage.getItem(LEGACY_LS_NOTES_KEY)
             || localStorage.getItem(NOTES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      try { localStorage.removeItem(LEGACY_LS_NOTES_KEY); } catch { /* ignore */ }
      try { localStorage.removeItem(NOTES_STORAGE_KEY); } catch { /* ignore */ }
      return parsed;
    }
    return null;
  } catch { return null; }
}

export default function CapacityReview() {
  // Phase 9: roles, tasks, and notes are now persisted to commercial.UserBlob
  // (per-user, multi-key) via useDbState. Each call hydrates from API on
  // mount, falls back to localStorage for one-shot legacy migration.
  const [people, setPeople, , peopleBlob, peopleHydrated] = useDbState(ROLES_STORAGE_KEY, DEFAULT_ROLES, loadLegacyRoles);

  // Default tasks state is empty {} — populated with seeds below once we
  // know the active person list.
  const [byPerson, setByPerson, , tasksBlob, tasksHydrated] = useDbState(STORAGE_KEY, {}, loadLegacyAll);
  const [notes, setNotes, , notesBlob, notesHydrated] = useDbState(NOTES_STORAGE_KEY, {}, loadLegacyNotes);

  // First-time seed: if hydrated people exists but byPerson has no entry
  // for them, seed defaults so the UI renders.
  useEffect(() => {
    if (!Array.isArray(people) || people.length === 0) return;
    if (Object.keys(byPerson).length > 0) return;
    const seeded = {};
    for (const p of people) seeded[p.id] = SEED_TASKS();
    setByPerson(seeded);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people]);

  const [activePerson, setActivePerson] = useState(null);
  // Set initial active person once people is hydrated.
  useEffect(() => {
    if (activePerson || !Array.isArray(people) || people.length === 0) return;
    setActivePerson(people[0]?.id);
  }, [people, activePerson]);
  const updateNotes = (text) => setNotes(prev => ({ ...prev, [activePerson]: text }));

  // Mutate role config — patch the active role's name / role label / hours.
  const updatePerson = (id, patch) => {
    setPeople(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const addPerson = () => {
    const id = rid();
    const newRole = { id, name: '', role: 'New role', standardHours: 38 };
    setPeople(prev => [...prev, newRole]);
    setByPerson(prev => ({ ...prev, [id]: [] }));
    setActivePerson(id);
  };

  const removePerson = (id) => {
    if (people.length <= 1) return;  // keep at least one role to switch to
    if (!window.confirm('Remove this role and all of its tasks/notes? This cannot be undone.')) return;
    setPeople(prev => prev.filter(p => p.id !== id));
    setByPerson(prev => { const next = { ...prev }; delete next[id]; return next; });
    setNotes(prev => { const next = { ...prev }; delete next[id]; return next; });
    if (activePerson === id) {
      const next = people.find(p => p.id !== id);
      if (next) setActivePerson(next.id);
    }
  };

  const person = people.find(p => p.id === activePerson) || people[0];
  const rows  = byPerson[activePerson] || [];
  const taskCount = rows.filter(isTask).length;

  const updateRow = (id, patch) => {
    setByPerson(prev => ({
      ...prev,
      [activePerson]: (prev[activePerson] || []).map(t => t.id === id ? { ...t, ...patch } : t),
    }));
  };

  const addTask = () => {
    setByPerson(prev => ({
      ...prev,
      [activePerson]: [...(prev[activePerson] || []), {
        id: rid(), task: '', duration: 0, frequency: 'weekly', category: 'Operational',
        value: 'Medium', roleFit: 'Aligned', action: 'Keep', notes: '',
      }],
    }));
  };

  const addSection = () => {
    setByPerson(prev => ({
      ...prev,
      [activePerson]: [...(prev[activePerson] || []), { id: rid(), kind: 'section', label: 'New section' }],
    }));
  };

  const removeRow = (id) => {
    setByPerson(prev => ({
      ...prev,
      [activePerson]: (prev[activePerson] || []).filter(t => t.id !== id),
    }));
  };

  // Move row at fromIdx so it sits before toIdx. Used by drag-and-drop.
  const moveRow = (fromIdx, toIdx) => {
    setByPerson(prev => {
      const list = [...(prev[activePerson] || [])];
      if (fromIdx < 0 || fromIdx >= list.length) return prev;
      const [picked] = list.splice(fromIdx, 1);
      const insertAt = Math.max(0, Math.min(list.length, toIdx > fromIdx ? toIdx - 1 : toIdx));
      list.splice(insertAt, 0, picked);
      return { ...prev, [activePerson]: list };
    });
  };

  const summary = useMemo(() => deriveSummary(rows), [rows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Load-failure banners for each of the three blobs we depend on —
          silent-fail bug fix (Phase 15). Shows the first one with an error;
          others can fail too but one banner is enough to action. */}
      <UserBlobErrorBanner blob={peopleBlob} noun="roles list"     hydrated={peopleHydrated} />
      <UserBlobErrorBanner blob={tasksBlob}  noun="capacity tasks" hydrated={tasksHydrated} />
      <UserBlobErrorBanner blob={notesBlob}  noun="capacity notes" hydrated={notesHydrated} />
      <div className="card" style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Weekly Time Audit &amp; Capacity Review</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Map a typical week to surface low-value work, role mismatches, and delegation opportunities.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {people.map(p => (
              <button
                key={p.id}
                onClick={() => setActivePerson(p.id)}
                className={activePerson === p.id ? 'pill-active' : 'pill-inactive'}
                title={p.name || '(no name yet)'}
              >
                {p.role || '—'}
              </button>
            ))}
            <button
              onClick={addPerson}
              className="pill-inactive"
              title="Add a new role"
              style={{ borderStyle: 'dashed' }}
            >+ Add role</button>
          </div>
        </div>
      </div>

      {/* Role editor — change the active role's label, owner name, and
          contracted weekly hours; all three persist to localStorage. */}
      {person && (
        <div className="card" style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <RoleField
            label="Role"
            value={person.role || ''}
            onChange={(v) => updatePerson(person.id, { role: v })}
            placeholder="e.g. National Sales Manager"
            minWidth={280}
          />
          <RoleField
            label="Person"
            value={person.name || ''}
            onChange={(v) => updatePerson(person.id, { name: v })}
            placeholder="Full name"
            minWidth={220}
          />
          <RoleField
            label="Standard hrs / week"
            type="number"
            value={person.standardHours ?? 38}
            onChange={(v) => updatePerson(person.id, { standardHours: v === '' ? 0 : Math.max(0, Number(v) || 0) })}
            placeholder="38"
            minWidth={120}
          />
          <button
            onClick={() => removePerson(person.id)}
            disabled={people.length <= 1}
            title={people.length <= 1 ? 'At least one role must remain' : 'Delete this role and all its data'}
            style={{
              background: 'transparent', color: 'var(--text-muted)',
              border: '1px solid var(--border-soft)', borderRadius: 6,
              padding: '8px 12px', fontSize: 12,
              cursor: people.length <= 1 ? 'not-allowed' : 'pointer',
              opacity: people.length <= 1 ? 0.5 : 1,
            }}
          >Delete role</button>
        </div>
      )}

      <SummaryCards summary={summary} person={person} onExport={() => exportToCsv(person, rows)} />

      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            {person.name || person.role || 'Untitled role'} &mdash; tasks ({taskCount})
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={addSection}
              title="Insert a section heading to group tasks"
              style={{
                background: 'var(--bg-input)', color: 'var(--text-body)',
                border: '1px solid var(--border-soft)', borderRadius: 6,
                padding: '6px 12px', fontSize: 12, cursor: 'pointer',
              }}
            >+ Section</button>
            <button
              onClick={addTask}
              style={{
                background: 'var(--accent)', color: '#fff', border: 'none',
                padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              }}
            >+ Add task</button>
          </div>
        </div>
        <TaskTable rows={rows} onUpdate={updateRow} onRemove={removeRow} onMove={moveRow} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Drag the <span style={{ fontFamily: 'monospace' }}>⋮⋮</span> handle on any row to reorder. Section headers also drag to regroup.
        </div>
      </div>

      {/* Per-person scratchpad — ideas, draft tasks, follow-ups before
          they get formalised in the table above. Saved per person to
          localStorage; switching tabs keeps each person's notes intact. */}
      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            {(person.name || person.role || 'Untitled').split(' ')[0]}&rsquo;s Notes
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Free text · auto-saves · per person
          </div>
        </div>
        <textarea
          value={notes[activePerson] || ''}
          onChange={(e) => updateNotes(e.target.value)}
          placeholder={`Drop ideas, recurring questions, or things to formalise into ${(person.name || person.role || 'this role').split(' ')[0]}'s task list later…`}
          rows={6}
          style={{
            width: '100%', minHeight: 120, resize: 'vertical',
            padding: '10px 12px', fontSize: 13, lineHeight: 1.5,
            fontFamily: 'inherit',
            background: 'var(--bg-elevated)', color: 'var(--text-body)',
            border: '1px solid var(--border)', borderRadius: 6,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      <div className="card" style={{ padding: '12px 16px', fontSize: 11, color: 'var(--text-muted)' }}>
        Edits save automatically to the database (per-user, synced across your devices). Export to CSV to share with the team for input,
        or paste into Excel/Sheets. The same person&rsquo;s rows and notes are preserved when switching tabs.
      </div>
    </div>
  );
}

function RoleField({ label, value, onChange, placeholder, minWidth = 200, type = 'text' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth, flex: type === 'number' ? '0 0 auto' : '1 1 auto' }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, fontWeight: 600 }}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 0.5 : undefined}
        style={{
          padding: '7px 10px', fontSize: 13,
          background: 'var(--bg-input)', color: 'var(--text-body)',
          border: '1px solid var(--border)', borderRadius: 6,
          outline: 'none', width: type === 'number' ? 100 : '100%',
        }}
      />
    </div>
  );
}

function SummaryCards({ summary, person, onExport }) {
  const card = {
    flex: 1, minWidth: 160, padding: '12px 16px',
    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8,
  };
  const labelStyle = { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
  const valStyle   = { fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' };
  const subStyle   = { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 };

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {(() => {
        const standard = person.standardHours || 38;
        const allocated = summary.totalHours;
        const available = standard - allocated;
        const utilPct = standard > 0 ? Math.round((allocated / standard) * 100) : 0;
        const overCapacity = available < 0;
        // Green when there's still spare capacity, amber when within 10% of full,
        // red once over the standard week.
        const barColor = overCapacity ? 'var(--danger)'
                       : utilPct >= 90 ? 'var(--warn)'
                       : 'var(--success)';
        return (
          <div style={{ ...card, minWidth: 200 }}>
            <div style={labelStyle}>Allocated / Standard Week</div>
            <div style={{ ...valStyle, color: overCapacity ? 'var(--danger)' : 'var(--text-primary)' }}>
              {allocated.toFixed(1)} <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>/ {standard} hrs</span>
            </div>
            {/* Capacity bar — hits the right edge of the card at 100% utilisation;
                anything beyond renders as a thin red overflow segment. */}
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, marginTop: 6, overflow: 'hidden', position: 'relative' }}>
              <div style={{
                width: `${Math.min(utilPct, 100)}%`, height: '100%',
                background: barColor, transition: 'width 0.2s, background 0.2s',
              }} />
            </div>
            <div style={{ ...subStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
              <span>{person.role}</span>
              <span style={{ color: barColor, fontWeight: 600 }}>
                {overCapacity
                  ? `${Math.abs(available).toFixed(1)} hrs over`
                  : `${available.toFixed(1)} hrs available`}
              </span>
            </div>
          </div>
        );
      })()}
      <div style={card}>
        <div style={labelStyle}>Strategic vs Reactive</div>
        <div style={valStyle}>{summary.strategicPct}% / {summary.reactivePct}%</div>
        <div style={subStyle}>strategic vs reactive share</div>
      </div>
      <div style={card}>
        <div style={labelStyle}>Low value / Duplicated</div>
        <div style={{ ...valStyle, color: summary.lowValuePct > 25 ? 'var(--danger)' : 'var(--text-primary)' }}>
          {summary.lowValuePct}%
        </div>
        <div style={subStyle}>{summary.lowValueHours.toFixed(1)} hrs flagged</div>
      </div>
      <div style={card}>
        <div style={labelStyle}>Role mismatch</div>
        <div style={{ ...valStyle, color: summary.mismatchPct > 20 ? 'var(--danger)' : 'var(--text-primary)' }}>
          {summary.mismatchPct}%
        </div>
        <div style={subStyle}>{summary.mismatchHours.toFixed(1)} hrs outside intended role</div>
      </div>
      <div style={card}>
        <div style={labelStyle}>Action queue</div>
        <div style={valStyle}>
          {summary.delegateCount + summary.simplifyCount + summary.eliminateCount}
        </div>
        <div style={subStyle}>
          {summary.delegateCount} delegate &middot; {summary.simplifyCount} simplify &middot; {summary.eliminateCount} eliminate
        </div>
      </div>
      <div style={{ ...card, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 140 }}>
        <div style={labelStyle}>Export</div>
        <button
          onClick={onExport}
          style={{
            background: 'var(--bg-input)', color: 'var(--text-body)',
            border: '1px solid var(--border-soft)', borderRadius: 6,
            padding: '8px 12px', fontSize: 12, cursor: 'pointer', marginTop: 4,
          }}
        >
          ↓ Download CSV
        </button>
        <div style={{ ...subStyle, marginTop: 6 }}>Opens in Excel</div>
      </div>
    </div>
  );
}

function deriveSummary(rows) {
  const tasks = rows.filter(isTask);
  const totalHours = tasks.reduce((s, t) => s + weeklyHours(t), 0);
  const hoursIn = (pred) => tasks.filter(pred).reduce((s, t) => s + weeklyHours(t), 0);
  const pct = (v) => totalHours > 0 ? Math.round((v / totalHours) * 100) : 0;
  const strategicHrs = hoursIn(t => t.category === 'Strategic');
  const reactiveHrs  = hoursIn(t => t.category === 'Reactive');
  const lowValueHrs  = hoursIn(t => t.value === 'Low' || t.value === 'Duplicated');
  const mismatchHrs  = hoursIn(t => t.roleFit === 'Mismatch');
  return {
    totalHours,
    strategicPct: pct(strategicHrs),
    reactivePct:  pct(reactiveHrs),
    lowValuePct:  pct(lowValueHrs),
    lowValueHours: lowValueHrs,
    mismatchPct:  pct(mismatchHrs),
    mismatchHours: mismatchHrs,
    delegateCount:  tasks.filter(t => t.action === 'Delegate').length,
    simplifyCount:  tasks.filter(t => t.action === 'Simplify').length,
    eliminateCount: tasks.filter(t => t.action === 'Eliminate').length,
  };
}

function TaskTable({ rows, onUpdate, onRemove, onMove }) {
  const cellPad = '6px 8px';
  const headStyle = { textAlign: 'left', fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: cellPad, fontWeight: 600 };
  const inputStyle = {
    width: '100%', padding: '4px 6px', fontSize: 12,
    background: 'transparent', color: 'var(--text-body)',
    border: '1px solid var(--border)', borderRadius: 4,
  };

  // Drag-and-drop state. dropTarget is the index where the dragged row would
  // land if dropped now (we render an indicator line above that row).
  const [dragIndex, setDragIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Per-section subtotals: keyed by the section row's id. A section "owns"
  // every task row beneath it until the next section (or end of list).
  // We pre-compute once so the section row render is O(1).
  const sectionSubtotals = (() => {
    const out = {};
    let currentSectionId = null;
    for (const r of rows) {
      if (isSection(r)) {
        currentSectionId = r.id;
        if (!out[r.id]) out[r.id] = 0;
      } else if (currentSectionId) {
        out[currentSectionId] = (out[currentSectionId] || 0) + weeklyHours(r);
      }
    }
    return out;
  })();
  const grandTotal = rows.reduce((s, r) => s + (isTask(r) ? weeklyHours(r) : 0), 0);

  if (!rows.length) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 12 }}>
      No rows yet — click <strong>+ Add task</strong> to start, or <strong>+ Section</strong> to add a heading.
    </div>;
  }

  function dragHandlers(idx) {
    return {
      draggable: true,
      onDragStart: (e) => {
        setDragIndex(idx);
        e.dataTransfer.effectAllowed = 'move';
        // Setting a drag image avoids the flicker of dragging just the handle.
        try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
      },
      onDragOver: (e) => {
        if (dragIndex == null) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Drop above or below this row based on cursor position
        const rect = e.currentTarget.getBoundingClientRect();
        const above = (e.clientY - rect.top) < rect.height / 2;
        setDropTarget(above ? idx : idx + 1);
      },
      onDrop: (e) => {
        e.preventDefault();
        if (dragIndex != null && dropTarget != null && dragIndex !== dropTarget && dragIndex + 1 !== dropTarget) {
          onMove(dragIndex, dropTarget);
        }
        setDragIndex(null);
        setDropTarget(null);
      },
      onDragEnd: () => { setDragIndex(null); setDropTarget(null); },
    };
  }

  // Drop indicator — a 2px line shown immediately before the row at index N.
  const dropIndicator = (n) => dropTarget === n ? (
    <tr key={`drop-${n}`} aria-hidden>
      <td colSpan={11} style={{ padding: 0, borderTop: '2px solid var(--accent)' }} />
    </tr>
  ) : null;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ ...headStyle, width: 18 }} />{/* drag handle column */}
            <th style={{ ...headStyle, minWidth: 220 }}>Task</th>
            <th style={{ ...headStyle, width: 80, textAlign: 'right' }}>Duration (hrs)</th>
            <th style={{ ...headStyle, width: 120 }}>Frequency</th>
            <th style={{ ...headStyle, width: 70, textAlign: 'right' }}>Hrs/wk</th>
            <th style={{ ...headStyle, width: 130 }}>Category</th>
            <th style={{ ...headStyle, width: 110 }}>Value</th>
            <th style={{ ...headStyle, width: 110 }}>Role fit</th>
            <th style={{ ...headStyle, width: 110 }}>Action</th>
            <th style={{ ...headStyle, minWidth: 200 }}>Notes</th>
            <th style={{ ...headStyle, width: 32 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((t, idx) => {
            const dragging = dragIndex === idx;
            const handlers = dragHandlers(idx);
            const rowStyle = {
              borderBottom: '1px solid var(--border)',
              opacity: dragging ? 0.4 : 1,
              transition: 'opacity 0.15s',
              background: dragging ? 'var(--bg-elevated)' : undefined,
            };
            return (
              <Fragment key={t.id}>
                {dropIndicator(idx)}
                {isSection(t) ? (() => {
                  const subtotal = sectionSubtotals[t.id] || 0;
                  const pct = grandTotal > 0 ? Math.round((subtotal / grandTotal) * 100) : 0;
                  return (
                  <tr key={t.id} style={{ ...rowStyle, background: 'var(--bg-elevated)' }} onDragOver={handlers.onDragOver} onDrop={handlers.onDrop}>
                    <td style={{ padding: cellPad, cursor: 'grab' }} draggable={true}
                        onDragStart={handlers.onDragStart} onDragEnd={handlers.onDragEnd}
                        title="Drag to reorder">
                      <span style={{ color: 'var(--text-muted)' }}>⋮⋮</span>
                    </td>
                    <td colSpan={3} style={{ padding: cellPad }}>
                      <input
                        style={{
                          width: '100%', padding: '6px 8px', fontSize: 13, fontWeight: 600,
                          color: 'var(--text-primary)', background: 'transparent',
                          border: 'none', borderBottom: '2px solid var(--border-soft)',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                        }}
                        value={t.label || ''}
                        placeholder="Section label (e.g. Customer & Sales)"
                        onChange={e => onUpdate(t.id, { label: e.target.value })}
                      />
                    </td>
                    <td style={{
                      padding: cellPad, textAlign: 'right',
                      fontSize: 13, fontWeight: 700, color: 'var(--text-primary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {subtotal.toFixed(2)}
                    </td>
                    <td colSpan={5} style={{ padding: cellPad, fontSize: 11, color: 'var(--text-muted)' }}>
                      hrs/wk &middot; {pct}% of week
                    </td>
                    <td style={{ padding: cellPad, textAlign: 'right' }}>
                      <button onClick={() => onRemove(t.id)} title="Delete section"
                        style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>×</button>
                    </td>
                  </tr>
                );})() : (
                  <tr key={t.id} style={rowStyle} onDragOver={handlers.onDragOver} onDrop={handlers.onDrop}>
                    <td style={{ padding: cellPad, cursor: 'grab' }} draggable={true}
                        onDragStart={handlers.onDragStart} onDragEnd={handlers.onDragEnd}
                        title="Drag to reorder">
                      <span style={{ color: 'var(--text-muted)' }}>⋮⋮</span>
                    </td>
                    <td style={{ padding: cellPad }}>
                      <input style={inputStyle} value={t.task || ''} placeholder="What is this task?"
                        onChange={e => onUpdate(t.id, { task: e.target.value })} />
                    </td>
                    <td style={{ padding: cellPad }}>
                      <input style={{ ...inputStyle, textAlign: 'right' }} type="number" min="0" step="0.25"
                        value={t.duration ?? t.hours ?? 0}
                        onChange={e => onUpdate(t.id, { duration: e.target.value === '' ? 0 : parseFloat(e.target.value), hours: undefined })} />
                    </td>
                    <td style={{ padding: cellPad }}>
                      <select style={inputStyle} value={t.frequency || 'weekly'}
                        onChange={e => onUpdate(t.id, { frequency: e.target.value })}>
                        {FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: cellPad, textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      {weeklyHours(t).toFixed(2)}
                    </td>
                    <td style={{ padding: cellPad }}>
                      <select style={inputStyle} value={t.category} onChange={e => onUpdate(t.id, { category: e.target.value })}>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: cellPad }}>
                      <select style={inputStyle} value={t.value} onChange={e => onUpdate(t.id, { value: e.target.value })}>
                        {VALUE_LEVELS.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: cellPad }}>
                      <select style={inputStyle} value={t.roleFit} onChange={e => onUpdate(t.id, { roleFit: e.target.value })}>
                        {ROLE_FITS.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: cellPad }}>
                      <select style={inputStyle} value={t.action} onChange={e => onUpdate(t.id, { action: e.target.value })}>
                        {ACTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: cellPad }}>
                      <input style={inputStyle} value={t.notes || ''} placeholder="Optional context"
                        onChange={e => onUpdate(t.id, { notes: e.target.value })} />
                    </td>
                    <td style={{ padding: cellPad, textAlign: 'right' }}>
                      <button onClick={() => onRemove(t.id)} title="Delete row"
                        style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>×</button>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {dropIndicator(rows.length)}
        </tbody>
      </table>
    </div>
  );
}

// CSV export — Excel opens it natively. Quote any field containing commas,
// quotes, or newlines per RFC 4180. Section headers become a "Group" column
// that propagates down to every task underneath them, so the whole file is
// still a flat tabular sheet.
function exportToCsv(person, rows) {
  const headers = ['Group', 'Task', 'Duration (hrs)', 'Frequency', 'Hours/Week', 'Category', 'Value', 'Role Fit', 'Action', 'Notes'];
  const out = [];
  let currentGroup = '';
  for (const r of rows) {
    if (isSection(r)) { currentGroup = r.label || ''; continue; }
    out.push([
      currentGroup,
      r.task,
      r.duration ?? r.hours ?? 0,
      (FREQ_BY_ID[r.frequency] || FREQ_BY_ID.weekly).label,
      weeklyHours(r).toFixed(2),
      r.category,
      r.value,
      r.roleFit,
      r.action,
      r.notes || '',
    ]);
  }
  const lines = [headers, ...out].map(r => r.map(csvCell).join(','));
  const csv = '\uFEFF' + lines.join('\r\n'); // BOM so Excel opens UTF-8 cleanly
  const today = new Date().toISOString().slice(0, 10);
  const fname = `capacity-review-${person.id}-${today}.csv`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvCell(v) {
  const s = String(v ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
